/**
 * Story Film 时间与版本分组辅助函数
 */

/**
 * 格式化相对时间（友好可读）
 * @param {string | number | Date | null | undefined} dateInput
 * @param {number} [nowMs]
 * @returns {string}
 */
export function formatRelativeTime(dateInput, nowMs = Date.now()) {
  if (dateInput === null || dateInput === undefined || dateInput === '') return '—'
  const date = new Date(dateInput)
  const time = date.getTime()
  if (Number.isNaN(time)) return '—'

  const diffSec = Math.floor((nowMs - time) / 1000)
  if (diffSec < 0) return '刚刚'
  if (diffSec < 60) return '刚刚'

  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}分钟前`

  const diffHour = Math.floor(diffMin / 60)
  if (diffHour < 24) return `${diffHour}小时前`

  const diffDay = Math.floor(diffHour / 24)
  if (diffDay === 1) {
    const pad = n => String(n).padStart(2, '0')
    return `昨天 ${pad(date.getHours())}:${pad(date.getMinutes())}`
  }
  if (diffDay < 7) return `${diffDay}天前`

  const pad = n => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * 格式化精确日期与时间 (YYYY-MM-DD HH:mm:ss)
 * @param {string | number | Date | null | undefined} dateInput
 * @returns {string}
 */
export function formatExactTime(dateInput) {
  if (dateInput === null || dateInput === undefined || dateInput === '') return '—'
  const date = new Date(dateInput)
  if (Number.isNaN(date.getTime())) return '—'

  const pad = n => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

/**
 * 为受理记录列表计算版本标记、时效与单集统计
 * @param {Array<Record<string, any>>} items
 * @param {number} [nowMs]
 * @returns {Array<Record<string, any>>}
 */
export function annotateExecutionVersions(items, nowMs = Date.now()) {
  if (!Array.isArray(items) || items.length === 0) return []

  // 1. 统计每个单集（优先按 episode_id，退回到 剧名+季+集）的最新 ID 与可播放最新 ID
  const episodeStats = new Map()

  for (const item of items) {
    const key = item.episode_id || `${item.film_title || 'unknown'}_${item.season_no ?? 1}_${item.episode_no ?? 1}`
    const subId = Number(item.submission_id) || 0
    const isReady = item.status === 'media_ready' || item.status === 'ok'

    if (!episodeStats.has(key)) {
      episodeStats.set(key, {
        latest_sub_id: subId,
        latest_ready_sub_id: isReady ? subId : null,
        total_count: 1,
      })
    } else {
      const stat = episodeStats.get(key)
      stat.total_count += 1
      if (subId > stat.latest_sub_id) {
        stat.latest_sub_id = subId
      }
      if (isReady && (stat.latest_ready_sub_id === null || subId > stat.latest_ready_sub_id)) {
        stat.latest_ready_sub_id = subId
      }
    }
  }

  // 2. 映射每个 item 附加元数据
  const annotated = items.map(item => {
    const key = item.episode_id || `${item.film_title || 'unknown'}_${item.season_no ?? 1}_${item.episode_no ?? 1}`
    const stat = episodeStats.get(key)
    const subId = Number(item.submission_id) || 0

    const is_latest = subId === stat.latest_sub_id
    const is_active_media = stat.latest_ready_sub_id !== null && subId === stat.latest_ready_sub_id

    return {
      ...item,
      is_latest,
      is_active_media,
      episode_total_count: stat.total_count,
      time_ago: formatRelativeTime(item.created_at, nowMs),
      formatted_time: formatExactTime(item.created_at),
    }
  })

  // 3. 严格按受理号倒序排列（保持真实的时间线流水）
  return annotated.sort((a, b) => (Number(b.submission_id) || 0) - (Number(a.submission_id) || 0))
}

/**
 * 将平铺记录按作品与单集进行结构化聚合
 * @param {Array<Record<string, any>>} annotatedItems
 * @returns {Array<Record<string, any>>}
 */
export function groupExecutionsByFilmAndEpisode(annotatedItems) {
  if (!Array.isArray(annotatedItems) || annotatedItems.length === 0) return []

  const filmMap = new Map()

  for (const item of annotatedItems) {
    const filmTitle = item.film_title || '未命名作品'
    if (!filmMap.has(filmTitle)) {
      filmMap.set(filmTitle, {
        film_title: filmTitle,
        total_submissions: 0,
        episode_map: new Map(),
      })
    }
    const filmGroup = filmMap.get(filmTitle)
    filmGroup.total_submissions += 1

    const epKey = item.episode_id || `s${item.season_no ?? 1}e${item.episode_no ?? 1}`
    if (!filmGroup.episode_map.has(epKey)) {
      filmGroup.episode_map.set(epKey, {
        episode_key: epKey,
        season_no: item.season_no ?? 1,
        episode_no: item.episode_no ?? 1,
        latest_item: item,
        active_item: item.is_active_media ? item : null,
        items: [item],
      })
    } else {
      const epGroup = filmGroup.episode_map.get(epKey)
      epGroup.items.push(item)
      if (item.is_active_media && !epGroup.active_item) {
        epGroup.active_item = item
      }
    }
  }

  return Array.from(filmMap.values()).map(film => ({
    film_title: film.film_title,
    total_submissions: film.total_submissions,
    episodes: Array.from(film.episode_map.values()).sort((a, b) => {
      if (a.season_no !== b.season_no) return a.season_no - b.season_no
      return a.episode_no - b.episode_no
    }),
  }))
}

